// useWebSocketConnection.ts
import { useState, useEffect, useCallback, useRef } from 'react'

type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'disconnected'
  | 'error'
  | 'disabled'

type MessageType = 'sent' | 'received' | 'history'

interface WebSocketMessage<T = any> {
  type: MessageType
  timestamp: Date
  data: T
}

interface WebSocketOptions {
  reconnectOnClose?: boolean
  reconnectInterval?: number
  maxReconnectAttempts?: number
  onMessage?: (data: any, event: MessageEvent) => void
  onOpen?: (event: Event) => void
  onClose?: (event: CloseEvent) => void
  onError?: (error: Event | Error) => void
}

interface WebSocketHookResult<T = any> {
  connectionStatus: ConnectionStatus
  messages: WebSocketMessage<T>[]
  sendMessage: (data: any) => boolean
  connect: () => void
  disconnect: () => void
  clearMessages: () => void
  isConnected: boolean
}

/**
 * React hook for managing a WebSocket connection
 */
const useWebSocketConnection = <T = any>(
  url: string | undefined,
  options: WebSocketOptions = {}
): WebSocketHookResult<T> => {
  const {
    reconnectOnClose = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
    onMessage,
    onOpen,
    onClose,
    onError,
  } = options

  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected')
  const [messages, setMessages] = useState<WebSocketMessage<T>[]>([])
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef<number>(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clean up function for WebSocket connection
  const cleanUp = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.close()
      wsRef.current = null
    }

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!url) {
      setConnectionStatus('disabled')
      return
    }

    // Clean up existing connection if any
    cleanUp()

    try {
      setConnectionStatus('connecting')
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = (event: Event) => {
        setConnectionStatus('connected')
        reconnectAttemptsRef.current = 0

        if (onOpen) {
          onOpen(event)
        }
      }

      ws.onmessage = (event: MessageEvent) => {
        let parsedData: any

        try {
          parsedData = JSON.parse(event.data)
        } catch (e) {
          parsedData = event.data
        }

        // Special handling for history messages
        if (
          parsedData &&
          parsedData.type === 'history' &&
          Array.isArray(parsedData.messages)
        ) {
          // Convert history messages to our format
          const historyMessages: WebSocketMessage<T>[] =
            parsedData.messages.map((msg: any) => ({
              type: 'history',
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              data: msg as T,
            }))

          // Add all history messages at once
          setMessages((prev) => [...prev, ...historyMessages])
        } else {
          // Regular message handling
          const newMessage: WebSocketMessage<T> = {
            type: 'received',
            timestamp: new Date(),
            data: parsedData as T,
          }

          setMessages((prev) => [...prev, newMessage])
        }

        if (onMessage) {
          onMessage(parsedData, event)
        }
      }

      ws.onclose = (event: CloseEvent) => {
        setConnectionStatus('disconnected')

        if (onClose) {
          onClose(event)
        }

        // Attempt to reconnect if enabled
        if (
          reconnectOnClose &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current += 1
          reconnectTimerRef.current = setTimeout(() => {
            connect()
          }, reconnectInterval)
        }
      }

      ws.onerror = (error: Event) => {
        setConnectionStatus('error')

        if (onError) {
          onError(error)
        }
      }
    } catch (error) {
      setConnectionStatus('error')
      console.error('WebSocket connection error:', error)

      if (onError && error instanceof Error) {
        onError(error)
      }
    }
  }, [
    url,
    reconnectOnClose,
    reconnectInterval,
    maxReconnectAttempts,
    onMessage,
    onOpen,
    onClose,
    onError,
    cleanUp,
  ])

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    setConnectionStatus('disconnecting')
    cleanUp()
    setConnectionStatus('disconnected')
  }, [cleanUp])

  // Send message through WebSocket
  const sendMessage = useCallback(
    (data: any): boolean => {
      if (!wsRef.current || connectionStatus !== 'connected') {
        return false
      }

      try {
        const message = typeof data === 'string' ? data : JSON.stringify(data)
        wsRef.current.send(message)

        const newMessage: WebSocketMessage<T> = {
          type: 'sent',
          timestamp: new Date(),
          data: data as T,
        }

        setMessages((prev) => [...prev, newMessage])
        return true
      } catch (error) {
        console.error('Error sending message:', error)
        return false
      }
    },
    [connectionStatus]
  )

  // Clear message history
  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  // Initial connection effect
  useEffect(() => {
    if (url) {
      connect()
    } else {
      cleanUp()
      setConnectionStatus('disabled')
    }

    // Clean up on unmount or URL change
    return () => {
      cleanUp()
    }
  }, [url, connect, cleanUp])

  return {
    connectionStatus,
    messages,
    sendMessage,
    connect,
    disconnect,
    clearMessages,
    isConnected: connectionStatus === 'connected',
  }
}

export default useWebSocketConnection
